import { useState, useCallback } from "react";
import { userService } from "../services/userService";
import { useApi } from "./useApi";

export const useUsers = () => {
  const [users, setUsers] = useState([]);
  const [raUsers, setRaUsers] = useState([])
  const [selectedUser, setSelectedUser] = useState(null);
  const { loading, error, executeRequest, clearError } = useApi();

  // Transform user data to ensure proper integer types
  const transformUserData = (user) => {
    if (!user) return null;

    return {
      ...user,
      isresearchanalyst: parseInt(user.isresearchanalyst) || 0,
      isblocked: parseInt(user.isblocked) || 0,
    };
  };

  // Transform array of users
  const transformUsersArray = (users) => {
    if (!Array.isArray(users)) return [];
    return users.map(transformUserData);
  };

  // Fetch all users
  const fetchUsers = useCallback(
    async (page = 1, limit = 50) => {
      try {
        const offset = (page - 1) * limit;
        const result = await executeRequest(
          userService.getAllUsers,
          limit,
          offset
        );

        const usersData = Array.isArray(result) ? result : result.data || [];
        const transformedUsers = transformUsersArray(usersData);
        setUsers(transformedUsers);
        return result;
      } catch (err) {
        console.error("Error fetching users:", err);
        setUsers([]);
      }
    },
    [executeRequest]
  );

  // Fetch all users
  const fetchRaUsers = useCallback(
    async (page = 1, limit = 50) => {
      try {
        const offset = (page - 1) * limit;
        const result = await executeRequest(
          userService.getRaUsers,
          limit,
          offset
        );

        const usersData = Array.isArray(result) ? result : result.data || [];
        const transformedUsers = transformUsersArray(usersData);
        setRaUsers(transformedUsers);
        return result;
      } catch (err) {
        console.error("Error fetching users:", err);
        setRaUsers([]);
      }
    },
    [executeRequest]
  );

  // Fetch single user
  const fetchUser = useCallback(
    async (id) => {
      try {
        const result = await executeRequest(userService.getUserById, id);
        const transformedUser = transformUserData(result);
        setSelectedUser(transformedUser);
        return transformedUser;
      } catch (err) {
        console.error("Error fetching user:", err);
        setSelectedUser(null);
      }
    },
    [executeRequest]
  );

  // Create user
  const createUser = useCallback(
    async (userData) => {
      try {
        // Ensure boolean fields are converted to integers
        const formattedData = {
          ...userData,
          isresearchanalyst: parseInt(userData.isresearchanalyst) || 0,
          isblocked: parseInt(userData.isblocked) || 0,
        };

        const result = await executeRequest(
          userService.createUser,
          formattedData
        );
        const transformedUser = transformUserData(result);
        setUsers((prev) => [...prev, transformedUser]);
        fetchUsers(); // Refresh the list
        return transformedUser;
      } catch (err) {
        console.error("Error creating user:", err);
        throw err;
      }
    },
    [executeRequest, fetchUsers]
  );

  // Update user
  const updateUser = useCallback(
    async (id, userData) => {
      try {
        // Ensure boolean fields are converted to integers
        const formattedData = {
          ...userData,
          isresearchanalyst: parseInt(userData.isresearchanalyst) || 0,
          isblocked: parseInt(userData.isblocked) || 0,
        };

        const result = await executeRequest(
          userService.updateUser,
          id,
          formattedData
        );
        const transformedUser = transformUserData(result);

        setUsers((prev) =>
          prev.map((user) =>
            user.userid === id ? { ...user, ...transformedUser } : user
          )
        );

        if (selectedUser && selectedUser.userid === id) {
          setSelectedUser({ ...selectedUser, ...transformedUser });
        }

        fetchUsers(); // Refresh the list
        return transformedUser;
      } catch (err) {
        console.error("Error updating user:", err);
        throw err;
      }
    },
    [executeRequest, selectedUser, fetchUsers]
  );

  // Delete user
  const deleteUser = useCallback(
    async (id) => {
      try {
        await executeRequest(userService.deleteUser, id);
        setUsers((prev) => prev.filter((user) => user.userid !== id));
        if (selectedUser && selectedUser.userid === id) {
          setSelectedUser(null);
        }
        return true;
      } catch (err) {
        console.error("Error deleting user:", err);
        throw err;
      }
    },
    [executeRequest, selectedUser]
  );

  // Search users
  const searchUsers = useCallback(
    async (query, filters = {}) => {
      try {
        const result = await executeRequest(
          userService.searchUsers,
          query,
          filters
        );
        const usersData = Array.isArray(result) ? result : result.data || [];
        const transformedUsers = transformUsersArray(usersData);
        setUsers(transformedUsers);
        return result;
      } catch (err) {
        console.error("Error searching users:", err);
        setUsers([]);
      }
    },
    [executeRequest]
  );

  // Clear selected user
  const clearSelectedUser = useCallback(() => {
    setSelectedUser(null);
  }, []);

  // ✅ REMOVED: Auto-fetch on mount - let component control pagination
  // useEffect(() => {
  //   fetchUsers();
  // }, [fetchUsers]);

  return {
    users,
    selectedUser,
    loading,
    error,
    fetchUsers,
    fetchUser,
    createUser,
    updateUser,
    deleteUser,
    searchUsers,
    clearSelectedUser,
    clearError,
    setRaUsers,
    raUsers,
    fetchRaUsers,
  };
};
